// Angular imports
import { Component, computed, inject, input, output } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
// App imports
import { AccountService } from 'src/app/main/shared/services/account.service';

import { TrackParameterName, TrackService } from '../../../track.service';

// Types
export interface CommentDialogState {
  show: boolean;
  dataIndex: number | null;
  position: number | null;
  oldValue: number | null;
  newValue: number | null;
  pixelX: number;
  pixelY: number;
}

// Constants
const COMMENT_DIALOG_WIDTH = 380; // Matches min-width in CSS
const COMMENT_DIALOG_HEIGHT = 240; // Approximate height with header, body, and footer
const COMMENT_DIALOG_OFFSET = 20;
const COMMENT_DIALOG_MARGIN = 10;

@Component({
  selector: 'app-track-plot-comment',
  imports: [ReactiveFormsModule],
  templateUrl: './track-plot-comment.component.html',
  styleUrl: './track-plot-comment.component.scss'
})
export class TrackPlotCommentComponent {
  // Services
  private readonly trackService = inject(TrackService);
  private readonly accountService = inject(AccountService);

  // Inputs
  readonly dataKey = input.required<TrackParameterName>();
  readonly commentDialogState = input.required<CommentDialogState>();

  // Outputs
  readonly commentSaved = output<{
    dataKey: TrackParameterName;
    index: number;
    comment: { date: string; content: string; author: string };
  }>();

  readonly commentClosed = output<void>();

  // Public Properties
  readonly commentInputControl = new FormControl('');

  /**
   * Computed signal for optimal comment dialog position
   * Calculates position only when dialog state changes, avoiding continuous reflows
   */
  readonly commentDialogPosition = computed(() => {
    const state = this.commentDialogState();

    let left = state.pixelX + COMMENT_DIALOG_OFFSET;
    let top = state.pixelY - COMMENT_DIALOG_HEIGHT / 2;

    // Cache window dimensions to avoid multiple reflows
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    // Adjust if going off right edge
    if (left + COMMENT_DIALOG_WIDTH > windowWidth) {
      left = state.pixelX - COMMENT_DIALOG_WIDTH - COMMENT_DIALOG_OFFSET;
    }

    // Adjust if going off left edge
    if (left < 0) {
      left = COMMENT_DIALOG_MARGIN;
    }

    // Adjust if going off top edge
    if (top < 0) {
      top = COMMENT_DIALOG_MARGIN;
    }

    // Adjust if going off bottom edge
    if (top + COMMENT_DIALOG_HEIGHT > windowHeight) {
      top = windowHeight - COMMENT_DIALOG_HEIGHT - COMMENT_DIALOG_MARGIN;
    }

    return {
      left: `${left}px`,
      top: `${top}px`
    };
  });

  /**
   * Formats the change summary for display and saving
   * @param state Comment dialog state containing old and new values
   * @returns Formatted change summary string
   */
  getChangesSummary(state: CommentDialogState): string {
    if (state.oldValue === null || state.newValue === null) {
      return 'Changes made';
    }

    const parameterInfo = this.trackService.parameterNameMappings[this.dataKey()];
    const unit = parameterInfo?.['trackParameter1']?.unit ?? '';

    // Format values based on parameter type
    let formattedOldValue: string;
    let formattedNewValue: string;

    if (this.dataKey() === 'curve_data') {
      // For curve data, always show radius values in meters (not curvature)
      // The chart displays curvature (1/R) but we edit radius (R) values
      formattedOldValue = `${Math.round(state.oldValue)}m`;
      formattedNewValue = `${Math.round(state.newValue)}m`;
    } else if (this.dataKey() === 'tunnel_factor_data') {
      // For tunnel factor, show with one decimal
      formattedOldValue = `${state.oldValue.toFixed(1)}`;
      formattedNewValue = `${state.newValue.toFixed(1)}`;
    } else {
      // For other parameters
      formattedOldValue = `${Math.round(state.oldValue)}${unit}`;
      formattedNewValue = `${Math.round(state.newValue)}${unit}`;
    }

    return `Changes made: ${formattedOldValue} → ${formattedNewValue}`;
  }

  /**
   * Saves the comment entered by the user to the data point
   * @param event Optional keyboard event (to handle Shift+Enter for new line)
   */
  saveComment(event?: Event): void {
    // Allow Shift+Enter to create new line in textarea
    if (event && event instanceof KeyboardEvent && event.shiftKey) {
      return;
    }
    event?.preventDefault();

    const state = this.commentDialogState();
    const commentText = this.commentInputControl.value?.trim();

    // Validation: ensure we have a valid data index
    if (state.dataIndex === null || state.dataIndex < 0) {
      console.error('Cannot save comment: invalid data index', state);
      this.closeCommentDialog();
      return;
    }

    try {
      // Format the change summary following track-comment pattern
      const changesSummary = this.getChangesSummary(state);

      // Build the full comment: changes first, then user comment (if any)
      let fullComment: string;
      if (commentText) {
        fullComment = '\n' + changesSummary + '\n\n' + commentText;
      } else {
        fullComment = '\n' + changesSummary;
      }

      const newComment = {
        date: new Date().toISOString(),
        content: fullComment,
        author: this.accountService.accountSummary()?.gid ?? 'Unknown'
      };

      console.debug(
        `Emitting comment for ${this.dataKey()} at index ${state.dataIndex}:`,
        fullComment
      );

      // Emit event to parent component to handle the update properly
      // This ensures the track signal is updated and change detection triggers
      this.commentSaved.emit({
        dataKey: this.dataKey(),
        index: state.dataIndex,
        comment: newComment
      });
    } catch (err) {
      console.error('Failed to prepare comment:', err);
    }

    this.closeCommentDialog();
  }

  /**
   * Closes the comment dialog and resets the form control
   */
  closeCommentDialog(): void {
    this.commentInputControl.reset();
    this.commentClosed.emit();
  }
}
